import {
  chatTransportErrorKind,
  type ChatConversation,
  type ChatMessage,
  type ChatTransport,
  type ChatTransportErrorKind,
} from './chatTransport';

import { ONE_MINUTE } from '@/constants/time';

/**
 * What one comparison found. Counts, kinds and attribute key names only:
 * never a body, an attribute value or anything else a message says, because
 * this is reported off the device.
 */
export type ShadowComparison = {
  conversationId: string;
  outcome: 'match' | 'mismatch' | 'shadow_failed';
  /** Set when the primary refused the conversation. */
  primaryKind?: ChatTransportErrorKind;
  /** Set when the shadow refused it. */
  shadowKind?: ChatTransportErrorKind;
  /** Messages both sides were expected to hold. */
  compared: number;
  /** In the primary, absent from the shadow. */
  missing: number;
  /** In the shadow, absent from the primary. */
  extra: number;
  idMismatch: number;
  bodyMismatch: number;
  attributeMismatch: number;
  /** Top-level attribute keys that differed, at most ten. */
  differingAttributeKeys: string[];
};

export type ShadowReporter = (comparison: ShadowComparison) => void;

/** The transport on trial. `forget` lets it drop a conversation once compared. */
export type ShadowCandidate = ChatTransport & { forget?: (conversationId: string) => void };

/** How many of the newest messages each side reads for a comparison. */
const COMPARED_PAGE_SIZE = 30;

/**
 * How old a message must be before a missing or different twin counts. Our
 * API copies Twilio's messages in from webhooks, so the newest ones can lag
 * by seconds; counting those would report replication delay as divergence.
 */
const SETTLE_MS = ONE_MINUTE;

const MAX_REPORTED_KEYS = 10;

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([first], [second]) => first.localeCompare(second));

    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(',')}}`;
  }

  return JSON.stringify(value ?? null);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function differingKeys(primary: unknown, shadow: unknown): string[] {
  const first = asRecord(primary);

  const second = asRecord(shadow);

  const keys = new Set([...Object.keys(first), ...Object.keys(second)]);

  return [...keys].filter((key) => stableStringify(first[key]) !== stableStringify(second[key]));
}

function emptyComparison(conversationId: string): ShadowComparison {
  return {
    conversationId,
    outcome: 'match',
    compared: 0,
    missing: 0,
    extra: 0,
    idMismatch: 0,
    bodyMismatch: 0,
    attributeMismatch: 0,
    differingAttributeKeys: [],
  };
}

/**
 * Compares two newest pages, ignoring what is too recent to have settled.
 *
 * Each side read only its newest page, so nothing below the oldest index of a
 * full page is compared: that page may simply have stopped before an older
 * message the other holds. A short page holds the whole conversation, so it
 * sets no bound. At the newest end both pages reach the end of their
 * conversation, and the settle window is what absorbs replication lag, so a
 * settled message one side lacks is a real difference.
 */
export function compareMessages(
  conversationId: string,
  primary: ChatMessage[],
  shadow: ChatMessage[],
  now: number,
  pageSize: number = COMPARED_PAGE_SIZE
): ShadowComparison {
  const comparison = emptyComparison(conversationId);

  const settledBefore = now - SETTLE_MS;

  /** An edit replicates with the same lag as a send, so the latest change is what must settle. */
  const isSettled = (message: ChatMessage) => {
    const changedAt = Math.max(
      message.createdAt?.getTime() ?? 0,
      message.updatedAt?.getTime() ?? 0
    );

    return changedAt <= settledBefore;
  };

  const oldestBound = (messages: ChatMessage[]) =>
    messages.length >= pageSize ? Math.min(...messages.map((message) => message.index)) : -Infinity;

  const floor = Math.max(oldestBound(primary), oldestBound(shadow));

  const inRange = (message: ChatMessage) => message.index >= floor;

  const primaryByIndex = new Map(primary.map((message) => [message.index, message]));

  const shadowByIndex = new Map(shadow.map((message) => [message.index, message]));

  const keys = new Set<string>();

  for (const message of primary) {
    const twin = shadowByIndex.get(message.index);

    if (!isSettled(message) || (twin && !isSettled(twin)) || !inRange(message)) {
      continue;
    }

    comparison.compared += 1;

    if (!twin) {
      comparison.missing += 1;

      continue;
    }

    if (twin.id !== message.id) {
      comparison.idMismatch += 1;
    }

    if ((twin.body ?? '') !== (message.body ?? '')) {
      comparison.bodyMismatch += 1;
    }

    const differing = differingKeys(message.attributes, twin.attributes);

    if (differing.length > 0) {
      comparison.attributeMismatch += 1;

      differing.forEach((key) => keys.add(key));
    }
  }

  for (const message of shadow) {
    if (isSettled(message) && inRange(message) && !primaryByIndex.has(message.index)) {
      comparison.extra += 1;
    }
  }

  comparison.differingAttributeKeys = [...keys].sort().slice(0, MAX_REPORTED_KEYS);

  const diverged =
    comparison.missing +
      comparison.extra +
      comparison.idMismatch +
      comparison.bodyMismatch +
      comparison.attributeMismatch >
    0;

  comparison.outcome = diverged ? 'mismatch' : 'match';

  return comparison;
}

/**
 * Parallel run of a candidate transport behind the one in charge.
 *
 * Every call is answered by the primary, exactly as it would be alone. The
 * first time each conversation is opened, the shadow opens it too and the two
 * are compared: the same refusal for a conversation the primary refused, and
 * the same messages for one it opened. What differs is reported; nothing the
 * shadow does reaches the chat, and a shadow failure is reported, never thrown.
 *
 * The shadow is never subscribed to, so a polling shadow never polls, and a
 * conversation it has compared is forgotten, so it holds nothing for the rest
 * of the session.
 */
export class ShadowChatTransport implements ChatTransport {
  private readonly compared = new Set<string>();

  constructor(
    readonly primary: ChatTransport,
    private readonly shadow: ShadowCandidate,
    private readonly report: ShadowReporter,
    private readonly now: () => number = Date.now
  ) {}

  async getConversation(conversationId: string): Promise<ChatConversation> {
    let conversation: ChatConversation;

    try {
      conversation = await this.primary.getConversation(conversationId);
    } catch (error) {
      this.onceFor(conversationId, () => this.compareRefusal(conversationId, error));

      throw error;
    }

    this.onceFor(conversationId, () => this.compareConversation(conversationId, conversation));

    return conversation;
  }

  shutdown(): Promise<void> {
    return Promise.allSettled([this.primary.shutdown(), this.shutdownShadow()]).then(
      () => undefined
    );
  }

  /** Stops the shadow alone, for when this wrapper is replaced but the primary lives on. */
  shutdownShadow(): Promise<void> {
    return this.shadow.shutdown();
  }

  private onceFor(conversationId: string, compare: () => Promise<ShadowComparison | undefined>) {
    if (this.compared.has(conversationId)) {
      return;
    }

    this.compared.add(conversationId);

    compare()
      .then((comparison) => {
        if (comparison) {
          this.report(comparison);
        }
      })
      .catch(() => undefined);
  }

  private async compareRefusal(
    conversationId: string,
    error: unknown
  ): Promise<ShadowComparison | undefined> {
    const primaryKind = chatTransportErrorKind(error);

    // A connection failure says nothing about the conversation, only the network.
    if (primaryKind === 'connection') {
      return undefined;
    }

    const comparison = { ...emptyComparison(conversationId), primaryKind };

    try {
      await this.shadow.getConversation(conversationId);

      return { ...comparison, outcome: 'mismatch' };
    } catch (shadowError) {
      const shadowKind = chatTransportErrorKind(shadowError);

      return {
        ...comparison,
        shadowKind,
        outcome: shadowKind === primaryKind ? 'match' : 'mismatch',
      };
    } finally {
      this.shadow.forget?.(conversationId);
    }
  }

  private async compareConversation(
    conversationId: string,
    conversation: ChatConversation
  ): Promise<ShadowComparison | undefined> {
    let primaryMessages: ChatMessage[];

    try {
      primaryMessages = (await conversation.getMessages(COMPARED_PAGE_SIZE)).items;
    } catch {
      return undefined;
    }

    try {
      const shadowConversation = await this.shadow.getConversation(conversationId);

      const { items } = await shadowConversation.getMessages(COMPARED_PAGE_SIZE);

      return compareMessages(conversationId, primaryMessages, items, this.now());
    } catch (error) {
      return {
        ...emptyComparison(conversationId),
        outcome: 'shadow_failed',
        shadowKind: chatTransportErrorKind(error),
      };
    } finally {
      this.shadow.forget?.(conversationId);
    }
  }
}
